import type { ReactNode } from 'react';
import styled from 'styled-components';

/* Элементы форм раздела Кадры поверх shared/ui/Input: многострочное поле,
   чекбокс с подписью, сетка полей, подсказка и ошибка поля. */

export const KadryTextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  resize: vertical;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3.5)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 14px;
  line-height: 1.5;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    box-shadow ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }
  &:hover:not(:focus) {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const CheckLabel = styled.label<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  input {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin: 2px 0 0;
    accent-color: ${({ theme }) => theme.colors.primary};
    cursor: inherit;
  }
`;

const CheckHint = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
  /** Переключатель из группы (`name` обязателен): выбор одного из вариантов. */
  radio?: boolean;
  name?: string;
}

/** Чекбокс (или переключатель группы) с подписью и подсказкой под ней. */
export function KadryCheckbox({
  checked,
  onChange,
  children,
  hint,
  disabled,
  radio,
  name,
}: CheckboxProps) {
  return (
    <CheckLabel $disabled={disabled}>
      <input
        type={radio ? 'radio' : 'checkbox'}
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        {children}
        {hint && <CheckHint>{hint}</CheckHint>}
      </span>
    </CheckLabel>
  );
}

/**
 * Сетка полей: 2 колонки на широких экранах, 1 — на узких. `$alignEnd` —
 * поля строки по нижнему краю: длинная подпись в две строки не сдвигает своё
 * поле ниже соседних (для строк без подсказок под полями).
 */
export const KadryFieldGrid = styled.div<{ $cols?: number; $alignEnd?: boolean }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols = 2 }) => $cols}, minmax(0, 1fr));
  gap: 0 ${({ theme }) => theme.spacing(4)};
  align-items: ${({ $alignEnd }) => ($alignEnd ? 'end' : 'stretch')};

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/** Группа чекбоксов в строку с переносом. */
export const KadryCheckRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(5)}`};
`;

export const KadryFieldHint = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const KadryFieldError = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.status.danger};
`;

/** Кнопки формы: справа, с переносом на узких экранах. */
export const KadryFormActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

/** Подзаголовок группы полей внутри формы. */
export const KadryFormSection = styled.fieldset`
  margin: 0 0 ${({ theme }) => theme.spacing(5)};
  padding: 0;
  border: none;
  min-width: 0;

  legend {
    padding: 0;
    margin-bottom: ${({ theme }) => theme.spacing(3)};
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

/** Необязательное целое/дробное число из строки поля: '' → null, мусор → NaN. */
export function kadryParseNumber(raw: string): number | null {
  const t = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : Number.NaN;
}
